import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { ChevronLeft, ChevronRight, X, Trophy, Users, ShoppingCart, Target, Calendar } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = [Trophy, Users, ShoppingCart, Target, Calendar];
const COLOR_MAP = ["text-primary", "text-secondary", "text-yellow-400", "text-green-400", "text-purple-400"];

const DEFAULT_STEPS = [
  { title: "Welcome to MSF Racing Fantasy!", subtitle: "Build your dream team and compete for glory",
    content: "MSF Racing Fantasy lets you become a team manager. Pick your drivers, cars, and tuners — then score points based on real race results!",
    tips: ["Two leagues: MSF Racing (cars) and MotoMSF (bikes)", "Each league has its own budget, team, and leaderboard", "Compete against other players for the top spot"] },
  { title: "Build Your Team", subtitle: "Select entities within your budget",
    content: "Choose your team within the budget limit. Every entity has a price — balance star performers with budget picks to maximize points.",
    tips: ["Racing: 2 Drivers + 3 Cars + 1 Tuner (200K budget)", "Moto: 2 Riders + 3 Bikes + 1 Tuner (150K budget)", "Changing your team after the initial pick costs 50 points penalty"] },
  { title: "Visit the Workshop", subtitle: "Purchase boosts to supercharge your team",
    content: "The Workshop lets you buy performance boosts (up to 2 per league) from your team budget.",
    tips: ["Boosts multiply, add, or protect your entity points", "Up to 2 boosts per league", "Boosts reset after each round — choose wisely!"] },
  { title: "Score Points", subtitle: "Your team earns points from real race results",
    content: "After each race round, points are assigned to all entities based on their real-world performance.",
    tips: ["Points are added after each race by the admin", "Check the leaderboard to see where you stand", "Click on other players to see their team selections"] },
  { title: "Race Calendar", subtitle: "Stay ahead of the competition",
    content: "The selection window opens before each round. Make sure to finalize your team before the deadline!",
    tips: ["Selection window locks before race weekends", "Check Results Center for race PDFs and standings", "Have fun and may the fastest team win!"] }
];

export default function WelcomeTutorial({ open, onClose, onDismissPermanently }) {
  const [step, setStep] = useState(0);
  const [steps, setSteps] = useState(DEFAULT_STEPS);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/rules-content`).then(res => {
      if (res.data?.tutorial_steps?.length) setSteps(res.data.tutorial_steps);
    }).catch(() => {});
  }, []);

  const current = steps[step] || steps[0];
  const IconComp = ICON_MAP[step] || Trophy;
  const color = COLOR_MAP[step] || "text-primary";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-950 border border-zinc-800 max-w-lg p-0 gap-0" onPointerDownOutside={(e) => e.preventDefault()}>
        <div className="flex h-1">
          {steps.map((s, i) => (
            <div key={s.title || `step-${i}`} className={`flex-1 transition-all duration-300 ${i <= step ? 'bg-primary' : 'bg-zinc-800'}`} />
          ))}
        </div>

        <button onClick={onClose} data-testid="tutorial-skip-btn" className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors z-10">
          <X size={20} />
        </button>

        <div className="px-8 py-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 flex items-center justify-center bg-zinc-900 border border-zinc-800">
              <IconComp size={24} className={color} />
            </div>
            <div>
              <h2 className="font-heading font-bold text-xl text-white">{current.title}</h2>
              <p className={`text-sm ${color}`}>{current.subtitle}</p>
            </div>
          </div>

          <p className="text-zinc-300 mb-5 leading-relaxed">{current.content}</p>

          <div className="space-y-2 mb-6">
            {(current.tips || []).map((tip, i) => (
              <div key={tip || `tip-${i}`} className="flex items-start gap-2 text-sm">
                <span className={`mt-1 w-1.5 h-1.5 shrink-0 ${color.replace('text-', 'bg-')}`} />
                <span className="text-zinc-400">{tip}</span>
              </div>
            ))}
          </div>

          <p className="text-xs text-zinc-600 text-center mb-4">Step {step + 1} of {steps.length}</p>
        </div>

        <div className="flex flex-col gap-3 px-8 pb-6">
          <div className="flex items-center justify-between">
            <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0} data-testid="tutorial-prev-btn"
              className={`flex items-center gap-1 px-4 py-2.5 font-heading font-bold uppercase text-sm transition-all ${step === 0 ? 'text-zinc-700 cursor-not-allowed' : 'text-zinc-400 hover:text-white'}`}>
              <ChevronLeft size={16} /> Back
            </button>

            {step < steps.length - 1 ? (
              <button onClick={() => setStep(s => s + 1)} data-testid="tutorial-next-btn"
                className="flex items-center gap-1 px-6 py-2.5 bg-primary text-white font-heading font-bold uppercase text-sm hover:opacity-80 transition-all">
                Next <ChevronRight size={16} />
              </button>
            ) : (
              <button onClick={onClose} data-testid="tutorial-finish-btn"
                className="px-6 py-2.5 bg-primary text-white font-heading font-bold uppercase text-sm hover:opacity-80 transition-all">
                Start Playing!
              </button>
            )}
          </div>

          <button onClick={onDismissPermanently} data-testid="tutorial-dont-show-btn" className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors text-center">
            Don't show me again
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
